import { applyServiceFee, centsToDollars } from "@outschool/business-rules";
import { programsRequireCreditBundle } from "@outschool/funding-programs-shared";
import { PaymentScheduleTypes } from "@outschool/gql-backend-generated";
import {
  AvailableSubscriptionPackagesQuery,
  AvailableSubscriptionPackagesQueryVariables,
  ChooseAPlanEnrollmentsQueryQuery,
  ChooseAPlanEnrollmentsQueryQueryVariables,
} from "@outschool/gql-frontend-generated";
import { useLookupIP } from "@outschool/iplookup-client";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import React from "react";

const EXCLUDED_STATES = ["AZ", "FL", "AR", "AL"];

function useIsStateExcluded() {
  const { ipInfo } = useLookupIP();

  const stateIsoCode =
    ipInfo?.country?.isoCode === "US"
      ? ipInfo?.subdivisions?.[0]?.isoCode
      : undefined;

  const isStateExcluded =
    stateIsoCode && EXCLUDED_STATES.includes(stateIsoCode);

  return {
    isStateExcluded,
  };
}

export function useRequiresMembershipForEnrollment() {
  const { isLoggedIn, currentUser, isEsaSession, esaSessionData } =
    useSession();
  const { isStateExcluded } = useIsStateExcluded();
  const enableRequireEsaMembershipForEnrollment = useFeatureFlag(
    "require-esa-membership-for-enrollment"
  );

  const currentUserRequiresMembershipForEnrollment =
    !!currentUser?.requiresMembershipForEnrollment;

  const requiresMembershipForEnrollment =
    (!isLoggedIn || currentUserRequiresMembershipForEnrollment) &&
    !isEsaSession &&
    !currentUser?.hasEsaMembership &&
    !isStateExcluded;

  const requiresEsaMembershipForEnrollment =
    enableRequireEsaMembershipForEnrollment &&
    isLoggedIn &&
    isEsaSession &&
    programsRequireCreditBundle.includes(
      esaSessionData?.shipping?.state ?? ""
    ) &&
    !currentUser?.hasEsaMembership;

  return {
    requiresMembershipForEnrollment,
    requiresEsaMembershipForEnrollment,
  };
}

export function useIsEligibleFor40PercentCouponUpsell() {
  const { isLoggedIn, currentUser, isEsaSession } = useSession();
  const { isStateExcluded } = useIsStateExcluded();

  const isExistingALaCarteBuyer = !!currentUser?.firstPaidEnrollment;

  // Audience for 40PERCENT coupon:
  // logged in users who have purchased a la carte classes, but do not have a membership (or any credits)
  // and are not ESA users or users in excluded states
  const isEligibleFor40PercentCouponUpsell =
    isLoggedIn &&
    isExistingALaCarteBuyer &&
    !currentUser?.hasSubscription &&
    !currentUser?.canPayWithSubscriptionCredits &&
    !isEsaSession &&
    !currentUser?.hasEsaMembership &&
    !isStateExcluded;

  return {
    isEligibleFor40PercentCouponUpsell,
  };
}

export function useShowCredits() {
  const {
    requiresMembershipForEnrollment,
    requiresEsaMembershipForEnrollment,
  } = useRequiresMembershipForEnrollment();
  const { currentUser } = useSession();
  return (
    requiresMembershipForEnrollment ||
    requiresEsaMembershipForEnrollment ||
    currentUser?.canPayWithSubscriptionCredits
  );
}

const SUBSCRIPTION_PACKAGES_QUERY = gql`
  query AvailableSubscriptionPackages {
    availableSubscriptionPackages {
      uid
      amountCents
      numCredits
      savingsPercentage
      savingsCents
      classPerWeekAvg
      numRollOverCredits
      numFreeCredits
    }
  }
`;

export function useSubscriptionPlans() {
  const { data, loading, error } = useQueryWithPreviousData<
    AvailableSubscriptionPackagesQuery,
    AvailableSubscriptionPackagesQueryVariables
  >(SUBSCRIPTION_PACKAGES_QUERY);
  return {
    subscriptionPlans: data?.availableSubscriptionPackages ?? [],
    loading,
    error,
  };
}

export const chooseAPlanEnrollmentsQuery = gql`
  query chooseAPlanEnrollmentsQuery($start: DateTime!, $end: DateTime!) {
    currentUser {
      uid
      activeEnrollments(start: $start, end: $end) {
        uid
        price_cents
        ongoingStartTime
        service_fee_percentage
        section {
          uid
          activity {
            uid
            title
            hasTeacherSchedule
          }
          meetings {
            uid
            start_time
            end_time
            paidAt
          }
        }
        paymentSchedule {
          uid
          scheduleType
          amountCents
        }
      }
    }
  }
`;

type ActiveEnrollmentsType = NonNullable<
  NonNullable<
    ChooseAPlanEnrollmentsQueryQuery["currentUser"]
  >["activeEnrollments"]
>;

export function useApproximateMonthlySpendFromActiveEnrollments(skip: boolean) {
  const { start, end } = React.useMemo(() => {
    const now = dayjs();
    return {
      start: now.subtract(1, "month").toDate(),
      end: now.toDate(),
    };
  }, []);

  const { data: activeEnrollmentsInThePastMonthData } =
    useQueryWithPreviousData<
      ChooseAPlanEnrollmentsQueryQuery,
      ChooseAPlanEnrollmentsQueryQueryVariables
    >(chooseAPlanEnrollmentsQuery, {
      variables: { start, end },
      skip,
    });

  const activeEnrollmentsInThePastMonth: ActiveEnrollmentsType =
    activeEnrollmentsInThePastMonthData?.currentUser?.activeEnrollments ?? [];

  if (activeEnrollmentsInThePastMonth.length > 0) {
    const monthlySpend = Math.round(
      centsToDollars(
        activeEnrollmentsInThePastMonth.reduce((total, enrollment) => {
          if (enrollment && enrollment.paymentSchedule) {
            // Club enrollments
            if (
              enrollment.paymentSchedule &&
              enrollment.paymentSchedule.amountCents &&
              enrollment.paymentSchedule.scheduleType ===
                PaymentScheduleTypes.MonthlyFirst
            ) {
              total += applyServiceFee(
                enrollment.paymentSchedule.amountCents,
                enrollment.service_fee_percentage
              );
              // Weekly enrollments (1:1)
            } else if (
              enrollment &&
              enrollment.price_cents &&
              enrollment?.section?.activity.hasTeacherSchedule
            ) {
              const numMeetings = enrollment.section.meetings.filter(
                meeting =>
                  dayjs(meeting.start_time).isBefore(end) &&
                  dayjs(meeting.start_time).isAfter(start) &&
                  !!meeting.paidAt
              ).length;
              total += applyServiceFee(
                enrollment.price_cents * numMeetings,
                enrollment.service_fee_percentage
              );
              // Weekly enrollments (courses)
            } else if (
              enrollment.paymentSchedule &&
              enrollment.paymentSchedule.amountCents &&
              enrollment.paymentSchedule.scheduleType ===
                PaymentScheduleTypes.WeeklySunday
            ) {
              total += applyServiceFee(
                enrollment.paymentSchedule.amountCents * 4,
                enrollment.service_fee_percentage
              );
            }
            // Weekly enrollments (Ongoing)
          } else if (
            enrollment &&
            enrollment.price_cents &&
            enrollment.ongoingStartTime
          ) {
            total += applyServiceFee(
              enrollment.price_cents * 4,
              enrollment.service_fee_percentage
            );
            // upfront enrollments
          } else if (enrollment && enrollment.price_cents) {
            total += applyServiceFee(
              enrollment.price_cents,
              enrollment.service_fee_percentage
            );
          }
          return total;
        }, 0)
      )
    );
    return monthlySpend;
  } else {
    return 0;
  }
}
